<template>
  <HiddenScrollbars>
    <div class="main-slider__parent">
      <div class="main-slider">
        <div
          v-if="!isRedirectInited"
          class="main-slider__wrapper swiper"
        >
          <div
            ref="sliderRef"
            v-swiper:swiperSlider="$options.static.swiperOption"
            class="main-slider__initial"
          >
            <div class="main-slider__container swiper-wrapper">
              <div
                v-for="(item, index) in mappedSlides"
                :key="item.background + index"
                class="swiper-slide"
                :class="slidesLuminance[index] && slidesLuminance[index].colorScheme"
              >
                <a
                  :href="item.deep_url || item.url"
                  class="main-slider__slide"
                  :data-id="!isLoggedIn ? `slider-swiper-register-link-${index}` : `slider-swiper-page-link-${index}`"
                  @click.prevent.stop="handleClick(item)"
                >
                  <ClientOnly>
                    <span
                      v-if="!isLoggedIn"
                      class="main-slider__signup-seo"
                      gtm-category="main_slider"
                      gtm-action="signup_click"
                    />
                  </ClientOnly>

                  <div class="main-slider__bottom-block">
                    <div
                      class="main-slider__text"
                      :class="[`main-slider__text-${index}`]"
                    >
                      <UiText
                        v-if="item.title"
                        class="slider-kicker"
                        font="slider-kicker"
                        color="neutral-900"
                        :text="item.title"
                        :data-is-desktop="$device.isDesktop"
                      />
                      <UiText
                        v-if="item.subtitle"
                        class="slider-heading"
                        font="slider-heading"
                        color="neutral-900"
                        :text="item.subtitle"
                      />
                    </div>
                    <UiBtn
                      tag="div"
                      color="primary"
                      :size="{default: 'm', desktop: 'l'}"
                      :text="$tc('buttons.details')"
                    />
                  </div>
                  <img
                    :src="$config.cloudUrl + item.background"
                    :data-index="index"
                    alt="Beton-Casino-slider"
                    class="main-slider__img-bg"
                    :class="[`main-slider__img-bg-${index}`]"
                    width="200"
                    height="200"
                    :loading="index ? 'lazy' : 'eager'"
                  />

                  <img
                    :src="$config.cloudUrl + item.art"
                    :data-index="index"
                    alt="Beton-Casino-slider"
                    class="main-slider__img"
                    :class="[`main-slider__img-${index}`]"
                    width="200"
                    height="200"
                    :loading="index ? 'lazy' : 'eager'"
                  />
                  <img
                    :src="$config.cloudUrl + item.background_mob"
                    :data-index="index"
                    alt="Beton-Casino-slider"
                    class="main-slider__img-bg-mob"
                    :class="[`main-slider__img-bg-${index}`]"
                    width="200"
                    height="200"
                    :loading="index ? 'lazy' : 'eager'"
                  />

                  <img
                    :src="$config.cloudUrl + item.art_mob"
                    :data-index="index"
                    alt="Beton-Casino-slider"
                    class="main-slider__img-mob"
                    :class="[`main-slider__img-${index}`]"
                    width="200"
                    height="200"
                    :loading="index ? 'lazy' : 'eager'"
                  />
                </a>
              </div>
            </div>
            <div class="swiper-nav">
              <UiBtn
                color="default"
                class="swiper-button-prev"
                icon-only
                icon="chevrons/chevron-left"
              />
              <UiBtn
                color="default"
                class="swiper-button-next"
                icon-only
                icon="chevrons/chevron-right"
              />
            </div>
          </div>
        </div>
      </div>
      <Feature v-if="hasFeatureCard" />
    </div>
  </HiddenScrollbars>
</template>

<script>
import { mapGetters } from 'vuex'
import Feature from './Feature'
import HiddenScrollbars from '@/components/Base/HiddenScrollbars'
import htmlTextParser from '@/util/helpers/htmlTextParser'
import { Swiper, Autoplay, Parallax } from 'swiper'
import ColorThief from 'colorthief'
import Service from './service'

Swiper.use([ Autoplay, Parallax, Autoplay])

const swiperOption = {
  observer: true,
  observeParents: true,
  slidesPerView: 1,
  loop: true,
  loopFillGroupWithBlank: true,
  preventClicks: true,
  preventClicksPropagation: false,
  threshold: 12,
  allowTouchMove: false,
  spaceBetween: 100,
  navigation: {
    nextEl: '.swiper-button-next',
    prevEl: '.swiper-button-prev',
  },
  parallax: true,
  autoplay: {
    delay: 3500,
    disableOnInteraction: false,
  },
}

export default {
  name: 'SliderWidget',
  components: { Feature, HiddenScrollbars },
  props: {
    slides: { type: Array, required: true },
    hasFeatureCard: { type: Boolean, default: false },
  },
  static: {
    swiperOption,
  },
  data() {
    return {
      slidesAmountWithCalculatedLumincance: 0,
      slidesLuminance: {},
      isRedirectInited: false,
    }
  },
  head() {
    if (!this.slides.length) {
      return {}
    }

    return {
      link: [
        { rel: 'preload', as: 'image', href: this.$config.cloudUrl + this.slides[0]?.background },
      ],
    }
  },
  computed: {
    ...mapGetters('base', ['get_theme']),
    ...mapGetters('user', ['isLoggedIn', 'isUserLoaded', 'isTokenInitialized' ]),
    isCalculatedLuminance() {
      return this.slidesAmountWithCalculatedLumincance >= this.mappedSlides.length
    },
    mappedSlides() {
      return this.slides.map(slide => {
        const text = this.$device.isMobile ? slide.text_mob[this.$i18n.locale] : slide.text[this.$i18n.locale]
        const [title, subtitle] = htmlTextParser(text)

        return {
          ...slide,
          title,
          subtitle,
        }
      })
    },
  },
  watch: {
    mappedSlides: {
      deep: true,
      immediate: true,
      handler() {
        this.$nextTick(() => {
          this.swiperSlider?.slideTo(1, 0)
          this.processImageColor()
        })
      },
    },
  },
  mounted() {
    if (this.mappedSlides.length)
      this.$nextTick(() => {
        this.processImageColor()
      })
  },
  methods: {
    getColor({ image, colorThief, index }) {
      try {
        const { isLight } = Service.calcColor({ image, colorThief })
        this.slidesLuminance = {
          ...this.slidesLuminance,
          [index]: { colorScheme: isLight ? 'light' : 'dark' },
        }
      }
      catch (e) {
        console.groupCollapsed('Slider Image Processing')
        console.warn(e)
        console.groupEnd()
        this.slidesLuminance = {
          ...this.slidesLuminance,
          [index]: { colorScheme: this.get_theme.isDark ? 'dark' : 'light' },
        }
      }
      finally {
        this.slidesAmountWithCalculatedLumincance+=1
      }

    },
    processImageColor() {
      if (process.server) return
      const colorThief = new ColorThief()
      const images = document.querySelectorAll('.main-slider__img-bg')

      for (const image of images) {
        if (this.isCalculatedLuminance) break
        const index = image.dataset.index
        if (image.complete) {
          this.getColor({ image, colorThief, index })
        }
        else {
          image.addEventListener('load', async () => {
            this.getColor({ image, colorThief, index })
          })
        }
      }

    },
    handleClick(slide) {
      if (!slide.url && !slide.deep_url) return

      this.isRedirectInited = true

      if (slide.url?.includes('http')) {
        window.open(slide.url, '_blank')
      }
      else if (slide.deep_url?.includes('http')) {
        window.open(slide.deep_url, '_blank')
      }
      else {
        this.$router.push(this.localePath(slide.url))
      }
    },
  },
}
</script>

<style lang="scss" src="./style.scss"></style>
